"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const ledgerInputClasses = "border-x-0 border-t-0 border-b-2 border-border rounded-md shadow-sm bg-transparent px-2 focus-visible:ring-0 focus-visible:border-primary transition-colors";
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="size-16 bg-primary/10 text-primary rounded-lg flex items-center justify-center mb-6 shadow-sm">
          <svg className="size-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-default text-2xl font-semibold mb-2">
          Inquiry Received
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                First Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John" className={ledgerInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Last Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Doe" className={ledgerInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={ledgerInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className={ledgerInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="serviceType" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Service of Interest
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={ledgerInputClasses}>
                    <SelectValue placeholder="Select a service..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="chef">Chef Services</SelectItem>
                  <SelectItem value="transport">Private Transport</SelectItem>
                  <SelectItem value="marine">Marine Charters</SelectItem>
                  <SelectItem value="tours">Curated Tours</SelectItem>
                  <SelectItem value="photography">
                    Photography & Location Scouting
                  </SelectItem>
                  <SelectItem value="other">Other / Multiple</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="number_of_people" render={({
        field
      }) => <FormItem className="md:col-span-1">
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Number of People
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={ledgerInputClasses}>
                    <SelectValue placeholder="Select count..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="1">1 Person</SelectItem>
                  <SelectItem value="2">2 People</SelectItem>
                  <SelectItem value="3">3 People</SelectItem>
                  <SelectItem value="4">4 People</SelectItem>
                  <SelectItem value="5">5 People</SelectItem>
                  <SelectItem value="6">6 People</SelectItem>
                  <SelectItem value="7+">7+ People</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="kids_ages" render={({
        field
      }) => <FormItem className="md:col-span-1">
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Children's Ages
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={ledgerInputClasses}>
                    <SelectValue placeholder="Select ages..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">No children</SelectItem>
                  <SelectItem value="under5">Under 5</SelectItem>
                  <SelectItem value="5-10">5-10 years</SelectItem>
                  <SelectItem value="11-17">11-17 years</SelectItem>
                  <SelectItem value="mixed">Mixed ages</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="dates" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Preferred Dates (Optional)
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g., October 15 - October 22" className={ledgerInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="details" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Additional Details
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us more about your requirements..." className={`min-h-[120px] resize-y ${ledgerInputClasses}`} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm text-center">{submitError}</p>
        </div>}

      <div className="mt-10 flex justify-center">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto min-w-[240px] rounded-md uppercase tracking-widest text-xs font-semibold">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : "Request Consultation"}
        </Button>
      </div>
    </Form>;
}
export default function PlanYourExperience() {
  return <section id="planyourexperience" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-default text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            Plan Your Experience
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-light">
            Begin curating your bespoke itinerary with IMI Elite Management.
            Provide your details below, and our concierge team will contact you
            to finalize arrangements.
          </p>
        </div>

        <div className="bg-card/50 backdrop-blur-sm border border-border/50 p-8 md:p-12 rounded-lg shadow-lg">
          <FormProvider formId="hospitality_booking_inquiry_form" sectionName="Plan Your Experience" defaultValues={{
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          serviceType: "",
          number_of_people: "",
          kids_ages: "",
          dates: "",
          details: ""
        }} submitText="Request Consultation" successMessage="Thank you for your inquiry. Our concierge team will review your request and contact you shortly to begin planning your experience.">
            <BookingForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}